import React from 'react';
import { Carousel } from 'react-responsive-carousel';
import { useNavigate } from 'react-router-dom';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import './WelcomePage.css';

import mission from './Images/mission.jpg';
import how_it_works from './Images/how_it_works.jpg';
import about_us from './Images/about_us.jpg';
import DeepToast from './DeepToast';
import new_mission from './Images/new_mission.png';
import { Target, Settings, Users } from "lucide-react";
import AppHeader from "./components/AppHeader";


const WelcomePage = () => {
  const navigate = useNavigate();

  const scrollToSection = () => {
    const section = document.getElementById('purpose');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };  

  return (

    <div>
       {/* Header */}
    <AppHeader titleSize="text-5xl" />

      {/* Hero Section */}
      <div className="w-full bg-accent text-white py-12 md:py-16 text-center">
        <Carousel
          showThumbs={false}
          showStatus={false}
          showArrows={false}
          infiniteLoop
          autoPlay
          interval={2000}
        >
          <div className="flex flex-col items-center justify-center space-y-4 pb-10">
            <h1 className="text-6xl font-bold text-primary">Welcome to DeepToast</h1>
            <p className="text-2xl text-primary">Helping Professors Create Smarter Question Banks</p>
          </div>
          <div className="flex flex-col items-center justify-center space-y-4 pb-10">
            <h1 className="text-6xl font-bold text-primary">Build Better Assessments</h1>
            <p className="text-2xl text-primary">Save time and generate intelligent quizzes instantly</p>
          </div>
          <div className="flex flex-col items-center justify-center space-y-4 pb-10">
            <h1 className="text-6xl font-bold text-primary">Customize. Control. Create.</h1>
            <p className="text-2xl text-primary">You're in charge of modules and question types</p>
          </div>
        </Carousel>

        {/* CTA Button */}
        <div className="mt-10 md:mt-14">
          <button
            onClick={() => navigate('/auth')}
            className="bg-primary hover:bg-primary text-white text-xl font-bold px-9 py-4 rounded-full shadow-md transition transform hover:scale-105"
          >
            Let’s Get Started
          </button>
        </div>
      </div>

      {/* Purpose Section */}
      <section id="purpose" className="bg-secondary w-full py-16 px-6 md:px-20">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start gap-12">
          <div className="text-xl font-bold tracking-widest text-black uppercase pt-2 md:pt-3">
            Purpose
          </div>
          <div className="space-y-4">
            <h2 className="text-2xl md:text-3xl font-bold leading-snug text-gray-900 max-w6xl">
              DeepToast is an AI-powered tool that helps educators automate question creation and generate
              custom assessments aligned with their course materials.
            </h2>
            <p className="text-base md:text-lg text-gray-700 max-w-3xl">
              DeepToast was created to help educators save time and effort by automating the process of question generation.
              Instead of manually drafting quizzes or exams, instructors can quickly produce high-quality, customized assessments aligned with their course content.
              Whether for formative practice or formal evaluation, DeepToast enables smarter, faster, and more consistent quiz creation — empowering faculty to focus on what matters most: teaching.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="bg-secondary w-full py-16">
        <div className="flex flex-col md:flex-row items-center">
          <div className="w-full md:w-[66%]">
            <img src={new_mission} alt="Mission" className="w-full h-[300px] md:h-[450px] object-cover" />
          </div>
          <div className="w-full md:w-[34%] h-[300px] md:h-[450px] px-6 md:px-12 overflow-y-auto">
            <div className="max-w-md py-4">
              <h3 className="text-3xl font-bold mb-4 text-gray-900">
                <Target className="inline-block mr-2 h-6 w-6 align-[-2px]" />
                Our Mission
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                Our mission is to empower educators with intelligent tools that simplify and elevate assessment design.
              </p>
              <a
                href="/mission"
                className="bg-[#1b2b45] text-white px-5 py-2 rounded-full font-semibold hover:scale-105 hover:shadow-lg transition"
              >
                Learn more 
              </a>
              
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-secondary w-full py-16">
        <div className="flex flex-col md:flex-row-reverse items-center">
          <div className="w-full md:w-[66%]">
            <img
              src={how_it_works}
              alt="How it works"
              className="w-full h-[300px] md:h-[450px] object-cover"
            />
          </div>
          <div className="w-full md:w-[34%] h-auto md:h-[450px] flex items-center px-6 md:px-12">
            <div className="max-w-md">
              <h3 className="text-3xl font-bold mb-4 text-gray-900">
                <Settings className="inline-block mr-2 h-6 w-6 align-[-2px]" />
                How It Works
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Just select a module, set your preferences, and let DeepToast do the rest — from AI generation to review and export.
              </p>
              <a
                href="/how-it-works"
                className="bg-[#1b2b45] text-white px-5 py-2 rounded-full font-semibold hover:scale-105 hover:shadow-lg transition"
              >
                See Full Workflow
              </a>
            </div>
          </div>
        </div>
      </section>



      {/* About Us */}
      <section className="bg-secondary w-full py-16">
        <div className="flex flex-col md:flex-row items-center">
          <div className="w-full md:w-[66%]">
            <img src={about_us} alt="About Us" className="w-full h-[300px] md:h-[450px] object-cover" />
          </div>
          <div className="w-full md:w-[34%] h-[300px] md:h-[450px] flex items-center px-6 md:px-12">
            <div className="max-w-md">
              <h3 className="text-3xl font-bold mb-4 text-gray-900">
                <Users className="inline-block mr-2 h-6 w-6 align-[-2px]" />
                About Us
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                We're a small team of educators, engineers, and designers with a passion for making academic tools better. 
                We believe in practical AI that saves time, not replaces people.
              </p>
              <button
                onClick={() => navigate('/team')}
                className="bg-[#1b2b45] text-white px-5 py-2 rounded-full font-semibold hover:scale-105 hover:shadow-lg transition"
              >
                Join our journey
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WelcomePage;
